import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { getMyPay, getTimeEntries, getPortalPaystubs, type MyPay, type TimeEntry, type PortalPaystub } from '@lib/api/portal';
import { computeHoursStats } from '../../lib/hoursStats';

const fmt = (n: number | string) => `$${Number(n).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const day = (s?: string | null) => (s || '').slice(0, 10);

// Financial content for workers: what I've earned, been paid, and am owed —
// rendered inside the merged Hours & Pay tab (TimesheetPage).
export const FinancialContent = () => {
  const [myPay, setMyPay] = useState<MyPay | null>(null);
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [paystubs, setPaystubs] = useState<PortalPaystub[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    Promise.allSettled([
      getMyPay().then(setMyPay),
      getTimeEntries().then(setEntries),
      getPortalPaystubs().then(setPaystubs),
    ]).then(results => {
      if (results.every(r => r.status === 'rejected')) setError('Failed to load. Please refresh.');
      setLoading(false);
    });
  };
  useEffect(() => { load(); }, []);

  const rate = myPay?.summary.hourly_rate ?? 0;

  // Earnings by month — approved hours × rate, last 6 months (single-series bars)
  const monthly = useMemo(() => {
    const out: { label: string; amount: number }[] = [];
    const now = new Date();
    for (let mb = 5; mb >= 0; mb--) {
      const m = new Date(now.getFullYear(), now.getMonth() - mb, 1);
      const from = `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, '0')}-01`;
      const to = `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, '0')}-31`;
      const hours = entries
        .filter(e => e.status === 'approved' && day(e.work_date) >= from && day(e.work_date) <= to)
        .reduce((s, e) => s + Number(e.hours), 0);
      out.push({ label: m.toLocaleDateString('en-CA', { month: 'short' }), amount: hours * rate });
    }
    return out;
  }, [entries, rate]);
  const maxMonthly = Math.max(...monthly.map(m => m.amount), 1);

  if (loading) return (
    <div className="space-y-3">
      {[1, 2, 3].map(i => <div key={i} className="h-24 animate-pulse rounded-xl bg-gray-100" />)}
    </div>
  );
  if (error) return <div className="px-4 py-6 text-sm text-red-600 flex items-center gap-2 bg-red-50 rounded-xl border border-red-200"><span className="material-symbols-outlined text-base">error</span>{error}</div>;

  const s = myPay?.summary;

  return (
      <div className="space-y-4 pb-4">

        {/* Owed hero */}
        {s && (
          <div className={`rounded-2xl p-5 text-center ${s.owed > 0.005 ? 'bg-[#C77D3F]' : 'bg-green-600'}`}>
            <p className="text-white/80 text-xs font-medium uppercase tracking-wide">Owed to you</p>
            <p className="text-white text-4xl font-bold mt-1">{fmt(s.owed)}</p>
            {s.pending_hours > 0 && (
              <p className="text-white/70 text-xs mt-1.5">
                + {s.pending_hours.toFixed(1)}h submitted, awaiting approval
              </p>
            )}
          </div>
        )}

        {/* Earned / paid tiles */}
        {s && (
          <div className="grid grid-cols-2 gap-2.5">
            <div className="bg-white rounded-xl border border-gray-200 p-3.5">
              <div className="flex items-center gap-1.5 text-gray-500">
                <span className="material-symbols-outlined text-base text-[#C77D3F]">trending_up</span>
                <span className="text-xs font-medium">Earned</span>
              </div>
              <p className="text-xl font-bold text-gray-900 mt-1">{fmt(s.earned)}</p>
              <p className="text-[11px] text-gray-400 mt-0.5">{s.approved_hours.toFixed(1)}h approved @ {fmt(s.hourly_rate)}/h</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-3.5">
              <div className="flex items-center gap-1.5 text-gray-500">
                <span className="material-symbols-outlined text-base text-[#C77D3F]">paid</span>
                <span className="text-xs font-medium">Paid to you</span>
              </div>
              <p className="text-xl font-bold text-gray-900 mt-1">{fmt(s.paid)}</p>
              <p className="text-[11px] text-gray-400 mt-0.5">{myPay!.payouts.length} payment{myPay!.payouts.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
        )}

        {/* Hours worked — the time behind the money */}
        {(() => {
          const h = computeHoursStats(entries);
          const cell = (label: string, value: string, sub?: string) => (
            <div className="text-center">
              <p className="text-[11px] text-gray-500">{label}</p>
              <p className="text-lg font-bold text-gray-900 leading-tight">{value}</p>
              {sub && <p className="text-[10px] text-gray-400">{sub}</p>}
            </div>
          );
          return (
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h2 className="font-semibold text-gray-900 text-sm mb-3 flex items-center gap-2">
                <span className="material-symbols-outlined text-[#C77D3F] text-xl">schedule</span>
                Hours worked
              </h2>
              <div className="grid grid-cols-3 gap-y-3">
                {cell('All time', `${h.allTime.toFixed(1)}h`, `${h.daysWorkedAllTime} days`)}
                {cell('This month', `${h.thisMonth.toFixed(1)}h`, `${h.daysWorkedMonth} day${h.daysWorkedMonth !== 1 ? 's' : ''}`)}
                {cell('This week', `${h.thisWeek.toFixed(1)}h`)}
                {cell('Avg / workday', `${h.avgPerWorkdayMonth.toFixed(1)}h`, 'this month')}
                {cell('Approved', `${h.approvedHours.toFixed(1)}h`)}
                {cell('Pending', `${h.pendingHours.toFixed(1)}h`, 'awaiting approval')}
              </div>
            </div>
          );
        })()}

        {/* Earnings by month — single-series bar chart */}
        {rate > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h2 className="font-semibold text-gray-900 text-sm mb-3">Earnings by month</h2>
            <div className="flex items-end gap-2 h-28">
              {monthly.map((m, i) => {
                const isPeak = m.amount === maxMonthly && m.amount > 0;
                const isCurrent = i === monthly.length - 1;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1 min-w-0" title={`${m.label}: ${fmt(m.amount)}`}>
                    {(isPeak || (isCurrent && m.amount > 0)) && (
                      <span className="text-[10px] font-semibold text-gray-700 leading-none">${Math.round(m.amount)}</span>
                    )}
                    <div
                      className={`w-full rounded-t ${isCurrent ? 'bg-[#C77D3F]' : 'bg-[#C77D3F]/45'}`}
                      style={{ height: `${Math.max((m.amount / maxMonthly) * 88, m.amount > 0 ? 4 : 2)}px` }}
                    />
                    <span className="text-[10px] text-gray-400">{m.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Payments received */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h2 className="font-semibold text-gray-900 text-sm mb-2 flex items-center gap-2">
            <span className="material-symbols-outlined text-[#C77D3F] text-xl">receipt_long</span>
            Payments received
          </h2>
          {!myPay || myPay.payouts.length === 0 ? (
            <p className="text-sm text-gray-500">No payments recorded yet.</p>
          ) : (
            <div className="divide-y divide-gray-50">
              {myPay.payouts.map(p => (
                <div key={p.id} className="flex items-center gap-3 py-2.5">
                  <span className="material-symbols-outlined text-green-600 text-lg">paid</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-gray-900">{fmt(p.amount)}</p>
                    {p.notes && <p className="text-xs text-gray-400 truncate">{p.notes}</p>}
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">
                      {new Date(day(p.paid_on) + 'T12:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                    <p className="text-[11px] text-gray-400 capitalize">{p.method}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Paystubs shortcut */}
        <Link
          to="/portal/employee/paystubs"
          className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3 active:bg-gray-50"
        >
          <div className="w-10 h-10 rounded-lg bg-[#C77D3F]/10 flex items-center justify-center flex-shrink-0">
            <span className="material-symbols-outlined text-[#C77D3F] text-xl">description</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 text-sm">Paystubs</p>
            <p className="text-xs text-gray-400">{paystubs.length} paystub{paystubs.length !== 1 ? 's' : ''} on file</p>
          </div>
          <span className="material-symbols-outlined text-gray-300">chevron_right</span>
        </Link>
      </div>
  );
};

// Money lives inside Hours & Pay now — old links land on the merged tab.
export const FinancialPage = () => <Navigate to="/portal/employee/timesheet?tab=money" replace />;
